/**
 * Authentication state store (Zustand).
 *
 * Manages the current user, JWT tokens, login/logout flows, and MFA challenge.
 */

import { create } from 'zustand';
import type { User, Role, MfaChallenge } from '@/types/auth';
import * as authApi from '@/api/auth';
import { queryClient } from './queryClient';
import { coreApiMode } from '@/config/deployment';

interface AuthState {
  /** The currently authenticated user, or null if logged out. */
  user: User | null;
  /** True while the initial auth check is in progress. */
  isLoading: boolean;
  /** True if the user is authenticated. */
  isAuthenticated: boolean;
  /** Pending MFA challenge (set during login when MFA is required). */
  mfaChallenge: MfaChallenge | null;

  /** Authenticate with email and password. Returns true if MFA is required. */
  login: (email: string, password: string) => Promise<boolean>;
  acceptAuthentication:(response:import('@/types/auth').LoginResponse)=>Promise<'authenticated'|'mfa'|'enrollment'>;
  /** Complete MFA login with a TOTP code. */
  verifyMfa: (code: string) => Promise<void>;
  /** Complete MFA login with a recovery code. */
  useRecoveryCode: (code: string) => Promise<void>;
  /** Clear tokens and user state. */
  logout: () => void;
  /** Load the current user profile from the API (called on app init). */
  loadUser: () => Promise<void>;
  /** Update the cached user after profile changes. */
  updateUser: (updates: Partial<User>) => void;
  /** Check if the user has at least one of the given roles. */
  hasRole: (...roles: Role[]) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  mfaChallenge: null,

  login: async (email, password) => {
    const response = await authApi.login({ email, password });
    return await get().acceptAuthentication(response)==='mfa';
  },
  acceptAuthentication:async(response)=>{
    if('enrollment_required' in response){sessionStorage.setItem('enrollment_token',response.enrollment_token);return 'enrollment';}
    if ('mfa_required' in response && response.mfa_required) {
      set({ mfaChallenge: response });
      return 'mfa';
    }

    // Normal login — store tokens and fetch user profile
    const tokens = response as import('@/types/auth').AuthTokens;
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);

    const user = await authApi.getMe();
    queryClient.clear();
    set({ user, isAuthenticated: true, isLoading: false, mfaChallenge: null });
    return 'authenticated';
  },

  verifyMfa: async (code) => {
    const { mfaChallenge } = get();
    if (!mfaChallenge) throw new Error('No MFA challenge pending');

    const tokens = await authApi.verifyMfa(mfaChallenge.mfa_token, code);
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);

    const user = await authApi.getMe();
    queryClient.clear();
    set({ user, isAuthenticated: true, isLoading: false, mfaChallenge: null });
  },

  useRecoveryCode: async (code) => {
    const { mfaChallenge } = get();
    if (!mfaChallenge) throw new Error('No MFA challenge pending');

    const tokens = await authApi.useRecoveryCode(mfaChallenge.mfa_token, code);
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);

    const user = await authApi.getMe();
    queryClient.clear();
    set({ user, isAuthenticated: true, isLoading: false, mfaChallenge: null });
  },

  logout: () => {
    const access = localStorage.getItem('access_token');
    if (coreApiMode && access) void authApi.logout(access).catch(()=>{});
    queryClient.clear();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    set({ user: null, isAuthenticated: false, isLoading: false, mfaChallenge: null });
  },

  loadUser: async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      set({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    try {
      const user = await authApi.getMe();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  updateUser: (updates) => {
    const { user } = get();
    if (user) {
      set({ user: { ...user, ...updates } });
    }
  },

  hasRole: (...roles) => {
    const { user } = get();
    return user !== null && roles.includes(user.role);
  },
}));
